import { useState } from 'react';
import { format } from 'date-fns';
import { Calendar, Pencil, Save, X } from 'lucide-react';
import { api } from '../../api/client';
import { formatPeriodo, duracaoTexto, parseDate } from '../../lib/dates';

// Card editável das datas da demanda. Aparece tanto na visão da Comunicação
// quanto na do Secretário. Backend libera a edição da DATA DA AÇÃO mesmo para
// etapas após "nova_demanda" — pedido da Laila pra remarcar mutirão / estender
// ultrassom sem precisar pedir alteração. Cada save vira evento na Timeline.
//
// `podeEditarPostagem` (só Comunicação/admin) adiciona a edição da POSTAGEM
// PREVISTA no mesmo card — pedido da Laila (03/07): o secretário mudava a data
// da ação, mas a data prevista de postagem ficava travada sem botão de editar.
export function EditarDatasAcao({ demanda, onSalvo, podeEditarPostagem = false }) {
  const [editando, setEditando] = useState(false);
  const [inicio, setInicio] = useState(demanda.data_acao || '');
  const [fim, setFim] = useState(demanda.data_acao_fim || '');
  const [postagem, setPostagem] = useState(demanda.data_postagem || '');
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState(null);

  const cancelar = () => {
    setInicio(demanda.data_acao || '');
    setFim(demanda.data_acao_fim || '');
    setPostagem(demanda.data_postagem || '');
    setEditando(false);
    setErro(null);
  };

  const salvar = async () => {
    setErro(null);
    if (inicio && fim && fim < inicio) {
      setErro('Data final não pode ser anterior à de início.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        data_acao: inicio || null,
        data_acao_fim: fim || null,
      };
      if (podeEditarPostagem) payload.data_postagem = postagem || null;
      await api.patch(`/demandas/${demanda.codigo}`, payload);
      setEditando(false);
      onSalvo?.();
    } catch (e) {
      setErro(e.data?.error || e.message);
    } finally { setSaving(false); }
  };

  const duracao = duracaoTexto(demanda.data_acao, demanda.data_acao_fim);
  const titulo = podeEditarPostagem ? 'Datas' : 'Data da ação';

  return (
    <div className="card">
      <div className="card-body" style={{ padding: 14 }}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
          <div className="row" style={{ gap: 6 }}>
            <Calendar size={14} color="var(--c-text-muted)" />
            <strong style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--c-text-muted)' }}>
              {titulo}
            </strong>
          </div>
          {!editando && (
            <button className="btn btn-ghost btn-sm" onClick={() => setEditando(true)} style={{ minHeight: 28, padding: '4px 10px' }}>
              <Pencil size={12} /> Editar
            </button>
          )}
        </div>

        {!editando ? (
          <div className="stack" style={{ gap: 10 }}>
            <div>
              {podeEditarPostagem && (
                <div className="detalhe-rotulo" style={{ marginBottom: 2 }}>Data da ação</div>
              )}
              {demanda.data_acao ? (
                <>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>
                    {formatPeriodo(demanda.data_acao, demanda.data_acao_fim)}
                    {duracao && <span className="muted" style={{ fontWeight: 400, marginLeft: 6, fontSize: 13 }}>· {duracao}</span>}
                  </div>
                  <p className="text-xs muted" style={{ marginTop: 4 }}>
                    Pode alterar a qualquer momento. A mudança fica registrada no histórico.
                  </p>
                </>
              ) : (
                <div className="muted text-sm">Sem data marcada. Clique em "Editar" para definir.</div>
              )}
            </div>

            {podeEditarPostagem && (
              <div>
                <div className="detalhe-rotulo" style={{ marginBottom: 2 }}>Postagem prevista</div>
                {demanda.data_postagem ? (
                  <div style={{ fontSize: 15, fontWeight: 600 }}>
                    {format(parseDate(demanda.data_postagem), 'dd/MM/yyyy')}
                  </div>
                ) : (
                  <div className="muted text-sm">Sem data prevista de postagem.</div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="stack" style={{ gap: 10 }}>
            {podeEditarPostagem && (
              <div className="detalhe-rotulo">Data da ação</div>
            )}
            <div className="grid-2" style={{ gap: 8 }}>
              <div className="field">
                <label>Início</label>
                <input type="date" className="input" value={inicio} onChange={(e) => setInicio(e.target.value)} />
              </div>
              <div className="field">
                <label>Fim (opcional)</label>
                <input type="date" className="input" value={fim} min={inicio || undefined} onChange={(e) => setFim(e.target.value)} disabled={!inicio} />
              </div>
            </div>
            <p className="text-xs muted">
              Use o campo "Fim" quando a ação durar vários dias (mutirão, semana de ultrassom, etc.).
            </p>

            {podeEditarPostagem && (
              <div className="field">
                <label>Postagem prevista (opcional)</label>
                <input type="date" className="input" value={postagem} onChange={(e) => setPostagem(e.target.value)} />
              </div>
            )}

            {erro && <div className="text-sm" style={{ color: 'var(--c-danger)' }}>{erro}</div>}
            <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={cancelar} disabled={saving}>
                <X size={12} /> Cancelar
              </button>
              <button className="btn btn-primary btn-sm" onClick={salvar} disabled={saving}>
                <Save size={12} /> {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
